// Filtering for the Data Sets list screen.
//
// A data set's type is free-form text entered on the definition screen, so filtering is a
// plain case-insensitive comparison rather than an enum. Kept out of the component so the
// matching rules can be tested directly.

// Filter value for data sets that have no type at all.
export const UNTYPED_FILTER = "__untyped__";

const typeOf = (dataSet) => (dataSet?.type ?? "").trim();

/**
 * Dropdown options for the type filter: every distinct type currently in the list, plus
 * "No type" when some data sets are untyped. Empty types are never offered as their own entry.
 *
 * @param {object[]} dataSets
 * @returns {{label: string, value: string}[]}
 */
export const getDataSetTypeOptions = (dataSets = []) => {
  const options = [
    { label: "All types", value: "all" },
    ...[...new Set(dataSets.map(typeOf).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
      .map((type) => ({ label: type, value: type })),
  ];

  if (dataSets.some((dataSet) => !typeOf(dataSet))) {
    options.push({ label: "No type", value: UNTYPED_FILTER });
  }

  return options;
};

/**
 * Filter data sets by type and by a free-text term.
 *
 * The term is matched against name, description, Chroma collection name and type, so typing
 * a type into the search box works as well as picking it from the dropdown.
 *
 * @param {object[]} dataSets
 * @param {{term?: string, type?: string}} [filters]
 * @returns {object[]}
 */
export const filterDataSets = (dataSets = [], { term = "", type = "all" } = {}) => {
  const needle = String(term ?? "").trim().toLowerCase();

  // Trim the requested type too, so "" and "   " both mean "no type" rather than one of them
  // silently matching nothing.
  const wantedType = type === UNTYPED_FILTER ? UNTYPED_FILTER : String(type ?? "all").trim();

  return (dataSets || []).filter((dataSet) => {
    const dataSetType = typeOf(dataSet);

    if (wantedType === UNTYPED_FILTER) {
      if (dataSetType) return false;
    } else if (wantedType !== "all" && dataSetType !== wantedType) {
      return false;
    }

    if (!needle) {
      return true;
    }

    return `${dataSet?.name ?? ""} ${dataSet?.description ?? ""} ${dataSet?.chroma_collection_name ?? ""} ${dataSetType}`
      .toLowerCase()
      .includes(needle);
  });
};
